// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import GroupIcon from '@mui/icons-material/Group';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  FormControlLabel,
  MenuItem,
  Tab,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  connect,
  Formik,
  FormikHelpers,
  FormikProps,
  FormikValues,
} from 'formik';
import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TabLabel from 'src/app/components/general/TabLabel';
import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';
import {
  trackModalUpdates,
  trackSaveAddQueue,
  trackSaveEditQueue,
} from 'src/common/userTracking';
import { useDebouncedCallback } from 'use-debounce/lib';
import * as Yup from 'yup';
import { LabelledOutline } from '../../../../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikTextField,
} from '../../../../../common/formik-material-ui';
import '../../../../../common/formik-material-ui/commaSeparatedEmails';
import { FormikSwitch } from '../../../../../common/formik-material-ui/FormikSwitch';
import { isUrl } from '../../../../../common/validators';
import { AdvancedIcon } from '../../../../../images/SettingsIcons';
import {
  createHoursString,
  Scheduler,
} from '../../../../components/CadenceSettings/Scheduler';
import {
  DefaultAction,
  getDefaultActionFields,
  getDefaultActionUrl,
  IActionType,
} from '../../../../components/dropdowns/DefaultAction';
import {
  getErrorTabIndex,
  isErrorInTab,
} from '../../../../components/modals/DevicesAndRoutingModal';
import { ModalWithStyles } from '../../../../components/modals/ModalWithStyles';
import { WizardModal } from '../../../../components/modals/WizardModal';
import { useSnackbarWithStyles } from '../../../../components/notifications/SnackbarProviderWithStyles';
import { RecordNow } from '../../../../components/recorder/RecordNow';
import { IQueueTeamsContent, QueueTeams } from './QueueTeams';
// import { valueFromAST } from 'node_modules/graphql';

// check if uri valid by reusing uri validator and useCallback to create uri closure only once
// TODO: move these utility to common folder
export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));
const useStyles = makeStyles((theme: Theme) => ({
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  indent: {
    marginLeft: theme.spacing(1),
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  tabs: {},
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabHeader: {
    width: '100%',
    '&.MuiTab-textColorInherit.Mui-disabled': {
      opacity: 1,
      '& .MuiTab-wrapper': {
        justifyContent: 'flex-start',
        margin: '10px 0',
      },
    },
  },
  tabIcon: {
    marginRight: 15,
  },
  tabError: {
    '& .MuiTabs-indicator': {
      backgroundColor: 'red',
    },
  },
}));

/**
 * Focus on invalid field using Formik
 * @see https://github.com/formium/formik/issues/146
 */
class ErrorFocus extends React.Component<any> {
  isObject(value: any) {
    return value && typeof value === 'object' && value.constructor === Object;
  }

  getKeysRecursively = (object: any): any => {
    if (!this.isObject(object)) {
      return '';
    }
    const currentKey = Object.keys(object)[0];
    if (!this.getKeysRecursively(object[currentKey])) {
      return currentKey;
    }
    return currentKey + '.' + this.getKeysRecursively(object[currentKey]);
  };

  componentDidUpdate(prevProps: any) {
    const { isSubmitting, isValidating, errors } = prevProps.formik;
    const keys = Object.keys(errors);
    if (keys.length > 0 && isSubmitting && !isValidating) {
      const selectorKey = this.getKeysRecursively(errors);
      const selector = `[id="${selectorKey}"], [name="${selectorKey}"] `;
      const errorElement: HTMLElement | null = document.querySelector(selector);
      errorElement?.focus();
    }
  }

  render() {
    return null;
  }
}

export const FormikErrorFocus = connect(ErrorFocus);

export interface IQueueEditFormInitialValues {
  submitQueueEditPayload: {
    name: string;
    email: string;
    maxduration: number;
    ringtimeout: number;
    greetingType: 'tts' | 'mp3' | 'record';
    // only show voicemail message if original type was tts
    greeting: string;
    musicType: 'mp3';
    holdMusic: string;
    voicemailType: 'tts' | 'mp3' | 'record';
    // only show voicemail message if original type was tts
    voicemail: string;
  };
  updateQueuePayload: {
    playWaitTime: number;
    autoAnswerInbound: string;
    inQueueIVR: '1' | '0';
    inQueueVoicemail: string;
    inQueueCallback: string;
    autoAnswerReturnedCall: string;
    DndAfterMissedCall: '1' | '0';
    subscribeOnLogin: '1' | '0';
    assignOwnerOnAnswer: '1' | '0';
    overrideOwnerOnAnswer: '1' | '0';
    initialAttempt:
      | 'ignoreOwnership'
      | 'routeToOwnerOnly'
      | 'routeToOwnerThenProceed';
    ringStrategy: 'linear' | 'simultaneous';
  };
  submitQueueVoicemailMp3File: {
    files: string;
  };
  submitQueueHoldMusicMp3File: {
    files: string;
  };
  submitQueueGreetingMp3File: {
    files: string;
  };
  defaultActionPayload: {
    type: string;
    ivr: string;
    group: string;
    user: string;
    queue: string;
  };
  queueTeams: IQueueTeamsContent[];
  useBusinessHoursEnabled: boolean;
  queueHours: any;
  closedActionPayload: {
    type: string;
    ivr: string;
    group: string;
    user: string;
    queue: string;
  };
}

/**
 * EventCallback trigger on user action
 *
 * @callback eventCallback
 * @param {Object} event Synthetic react event
 */

interface IQueueEditForm {
  id?: string;
  businessid?: string | number;
  show: boolean;
  queue: any;
  isLoading: boolean;
  onSubmit: any;
  onHide: () => void;
  formikRef?: MutableRefObject<FormikProps<any> | undefined>;
  formikValidator?: (values: FormikValues) => void | Record<string, unknown>;
  formikValidatorDebounceTimeout?: number;
  render?: React.FC<{ formik: FormikProps<any> }>;
  businessLoadable: any;
  businessCached: any;
  onTeamChange: () => void;
  showMembersTab?: boolean;
}

/**
 * Queue form to edit settings/options
 *
 * @param {Object} props
 * @param {Object} props.id Queue ID
 * @param {Object} props.queue Object containing current queue settings/options
 * @param {boolean} props.isLoading Set to true to show spinner
 * @param {eventCallback} props.onSubmit Callback to trigger on submit
 * @param {eventCallback} props.onHide Function to hide modal
 *
 * @see  https://stackoverflow.com/questions/48901016/how-to-set-change-field-value-from-external-user-action-react-final-form final-form mutators
 */
export function QueueEditForm({
  id,
  businessid,
  show,
  queue,
  isLoading,
  onSubmit,
  onHide,
  render,
  formikRef,
  formikValidator,
  businessLoadable,
  businessCached,
  formikValidatorDebounceTimeout = 250,
  onTeamChange,
  showMembersTab = false,
}: IQueueEditForm) {
  const classes = useStyles();
  const { treatment: isSimultaneousRingTreatment } = useFeatureFlag(
    'TEL-232-queue-simultaneous-ring'
  );
  const isSimultaneousRingEnabled = ['on', 'control'].includes(
    isSimultaneousRingTreatment
  );

  const [tabIndex, setTabIndex] = useState(0);
  const handleChange = (formik: any) => async (event: any, newIndex: any) => {
    setTabIndex(+newIndex);
  };
  React.useEffect(() => {
    showMembersTab && tabIndex !== 3 && setTabIndex(3);
  }, [showMembersTab, setTabIndex]);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [filenames, setFilenames] = useState({
    voicemail: isUrl(queue.voicemail)
      ? queue.voicemail.split('/').slice(-1).pop()
      : '', // filename from uri/path
    greeting: isUrl(queue.greeting)
      ? queue.greeting.split('/').slice(-1).pop()
      : '', // filename from uri/path
    holdMusic: isUrl(queue.holdMusic)
      ? queue.holdMusic.split('/').slice(-1).pop()
      : '', // filename from uri/path
  }); // fields cannot be null/undefined
  const [filepaths, setFilepaths] = useState({
    voicemail: isUrl(queue.voicemail) ? queue.voicemail : '',
    greeting: isUrl(queue.greeting) ? queue.greeting : '',
    holdMusic: isUrl(queue.holdMusic) ? queue.holdMusic : '',
  }); // fields cannot be null/undefined
  const [uploadedFiles, setUploadedFiles] = useState({});

  const { callback: debouncedCallback } = formikValidator
    ? useDebouncedCallback(formikValidator, formikValidatorDebounceTimeout)
    : { callback: undefined };
  const handleRecording = (type: string) => (files: any) => {
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    });
  };

  /**
   * Update filename on input change
   * Set filename to name of file or '# files' if multiple files
   *
   * NOTE: input element value must be set with event.target.value
   *
   * @param {Object} event Synthetic React event
   */
  const onChangeFile = (type: string) => ({ target: { files } }: any) => {
    setFilenames({
      ...filenames,
      // voicemail or greeting
      [type]:
        files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? '',
    });
    setFilepaths({
      ...filepaths,
      [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
    });
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    }); // take first file
  };

  const onSubmitHandler = (
    values: IQueueEditFormInitialValues,
    formikHelpers: FormikHelpers<IQueueEditFormInitialValues>
  ) => {
    trackUpdates(values, initialValues, currentTabTitle);
    onSubmit({
      ...values,
      uploadedFiles, // send uploaded files to caller since input type=file cannot be programmatically updated
      initialValues,
      formikHelpers,
    });
  };

  // set initial values and update when new data arrives
  useEffect(() => {
    setFilenames({
      voicemail: isUrl(queue.voicemail)
        ? queue.voicemail.split('/').slice(-1).pop()
        : '', // filename from uri/path
      greeting: isUrl(queue.greeting)
        ? queue.greeting.split('/').slice(-1).pop()
        : '', // filename from uri/path
      holdMusic: isUrl(queue.holdMusic)
        ? queue.holdMusic.split('/').slice(-1).pop()
        : '', // filename from uri/path
    });
    setFilepaths({
      voicemail: isUrl(queue.voicemail) ? queue.voicemail : '',
      greeting: isUrl(queue.greeting) ? queue.greeting : '',
      holdMusic: isUrl(queue.holdMusic) ? queue.holdMusic : '',
    });
  }, [queue]);
  const tabNames = [
    {
      title: 'Settings',
      icon: SettingsApplicationsIcon,
    },
    {
      title: 'Queue Hours',
      icon: QueryBuilderIcon,
    },
    {
      title: 'Messages',
      icon: MailOutlineIcon,
    },
    {
      title: 'Members',
      icon: GroupIcon,
    },
    {
      title: 'Advanced',
      icon: AdvancedIcon,
    },
  ];
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);
  // Validation schema (useMemo to prevent recreation)
  // Put here to be closer to initial values
  const QueueEditSchema = useMemo(
    () =>
      Yup.object({
        submitQueueEditPayload: Yup.object({
          name: Yup.string().required('Required'),
          email: Yup.string()
            .commaSeparatedEmails('Must be a email')
            .required('Required'),
          maxduration: Yup.number()
            .typeError('Must be a number')
            .required('Required'),
          ringtimeout: Yup.number()
            .typeError('Must be a number')
            .max(60, 'Must be at most 60')
            .min(5, 'must be at least 5')
            .required('Required'),
        }),
        submitQueueGreetingMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
        submitQueueVoicemailMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
        submitQueueHoldMusicMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
      }),
    []
  );

  // get all defaults based on queue
  const {
    defaultType,
    ivrDefaultValue,
    groupDefaultValue,
    userDefaultValue,
    queueDefaultValue,
  } = getDefaultActionFields(queue.defaultAction, true, undefined, id);
  const closedActionFields = getDefaultActionFields(
    queue.closedAction,
    true,
    undefined,
    id
  );
  const tabToFields = [
    ['submitQueueEditPayload', 'defaultActionPayload'],
    ['closedActionPayload', 'queueHours'],
    [
      'updateQueuePayload',
      'submitQueueVoicemailMp3File',
      'submitQueueHoldMusicMp3File',
      'submitQueueGreetingMp3File',
    ],
    ['queueTeams'],
  ];
  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const handleShowScheduler = () => setShowScheduler(true);
  const onHideHours = () => {
    setShowScheduler(false);
  };

  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours: any) => {
    schedulableHoursCached.current = hours;
  };
  // initialized values for Formik
  const initialValues = useMemo<IQueueEditFormInitialValues>(
    () => ({
      submitQueueEditPayload: {
        name: queue.queuename,
        email: queue.email,
        maxduration: queue.maxduration,
        ringtimeout: queue.ringtimeout,
        greetingType: queue.greetingType,
        // only show voicemail message if original type was tts
        greeting: queue.greetingType === 'tts' ? queue.greeting : '',
        musicType: queue.musicType,
        holdMusic: queue.holdMusic,
        voicemailType: queue.voicemailType,
        // only show voicemail message if original type was tts
        voicemail: queue.voicemailType === 'tts' ? queue.voicemail : '',
      },
      updateQueuePayload: {
        playWaitTime: queue.playWaitTime,
        autoAnswerInbound: queue.autoAnswerInbound,
        inQueueIVR: queue.inQueueIVR,
        inQueueVoicemail: queue.inQueueVoicemail,
        inQueueCallback: queue.inQueueCallback,
        autoAnswerReturnedCall: queue.autoAnswerReturnedCall,
        DndAfterMissedCall: queue.DndAfterMissedCall,
        subscribeOnLogin: queue.subscribeOnLogin,
        assignOwnerOnAnswer: queue.assignOwnerOnAnswer,
        overrideOwnerOnAnswer: queue.overrideOwnerOnAnswer,
        initialAttempt: queue.initialAttempt,
        ringStrategy: queue.ringStrategy,
      },
      submitQueueVoicemailMp3File: {
        files: '',
        name: filenames?.voicemail ?? '',
      },
      submitQueueHoldMusicMp3File: {
        files: '',
        name: filenames?.holdMusic ?? '',
      },
      submitQueueGreetingMp3File: {
        files: '',
        name: filenames?.greeting ?? '',
      },
      defaultActionPayload: {
        type: defaultType ?? '',
        ivr: ivrDefaultValue ?? '',
        group: groupDefaultValue ?? '',
        user: userDefaultValue ?? '',
        queue: queueDefaultValue ?? '',
      },
      queueTeams: queue.queueTeams,
      useBusinessHoursEnabled: !!Number(queue.useBusinessHoursEnabled),
      queueHours: queue.queueHours,
      closedActionPayload: {
        type: closedActionFields?.defaultType ?? '',
        ivr: closedActionFields?.ivrDefaultValue ?? '',
        group: closedActionFields?.groupDefaultValue ?? '',
        user: closedActionFields?.userDefaultValue ?? '',
        queue: closedActionFields?.queueDefaultValue ?? '',
      },
    }),
    [
      queue,
      defaultType,
      ivrDefaultValue,
      groupDefaultValue,
      userDefaultValue,
      queueDefaultValue,
    ]
  );
  function trackUpdates(values, initialValues, currentTabTitle) {
    trackModalUpdates(
      values,
      initialValues,
      'Queue Settings Updated',
      currentTabTitle
    );
  }
  return (
    <Formik
      innerRef={formikRef as MutableRefObject<FormikProps<any>>}
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={QueueEditSchema}
      validateOnBlur={false} // validate onChange already on
      validate={debouncedCallback}
      onSubmit={onSubmitHandler}
    >
      {(formik) => {
        function handleSubmit() {
          if (!createHoursString(schedulableHoursCached.current)) {
            enqueueSnackbarWithStyles({
              message: 'Please select a time range',
              alertProps: {
                severity: 'warning',
              },
            });
          } else {
            formik.setFieldValue('queueHours', schedulableHoursCached.current);
            onHideHours();
          }
        }
        const queueHoursString = useMemo<string[]>(
          () =>
            createHoursString(
              formik.values.useBusinessHoursEnabled
                ? businessCached.current?.businessDetails?.businessHours
                : formik.values.queueHours
            ).split(','),
          [
            showBusinessHours,
            setShowBusinessHours,
            formik.values.useBusinessHoursEnabled,
            formik.values.queueHours,
            businessLoadable,
          ]
        );
        return (
          <form
            className="form form-label-right"
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <TabContext value={String(tabIndex)}>
              <WizardModal
                className="queueModal"
                size="lg"
                title={queue.queuename || 'Add New Queue'}
                show={show}
                onHide={() => {
                  onHide();
                }}
                disableSubmit={!formik.dirty}
                isSubmitting={formik.isSubmitting}
                onSubmit={() => {
                  queue.queuename ? trackSaveEditQueue() : trackSaveAddQueue();
                  formik.handleSubmit();
                }}
                isLoading={isLoading}
                navigation={
                  <>
                    <Tab
                      className={clsx(classes.tab, classes.tabHeader)}
                      label={queue.queuename || 'Add New Queue'}
                      disabled={true}
                    />
                    <TabList
                      onChange={handleChange(formik)}
                      aria-label="Queue Form"
                      orientation="vertical"
                      variant="standard"
                      className={clsx(classes.tabs, {
                        [classes.tabError]: !formik.isValid,
                      })}
                    >
                      {tabNames.map((tabItem, index) => (
                        <Tab
                          {...{ name: tabItem.title }}
                          key={index}
                          className={classes.tab}
                          label={
                            <TabLabel
                              index={index}
                              formik={formik}
                              tabToFields={tabToFields}
                              tabItem={tabItem}
                            />
                          }
                          value={String(index)}
                        />
                      ))}
                    </TabList>
                  </>
                }
                dialogActions={
                  <>
                    <Button
                      className={classes.button}
                      disabled={formik.isSubmitting}
                      variant="contained"
                      color="inherit"
                      disableElevation={true}
                      onClick={onHide}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      className={clsx(classes.button)}
                      variant="contained"
                      disableElevation={true}
                      disabled={formik.isSubmitting || !formik.dirty}
                      onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                      ) => {
                        // trigger show errors if errors exists
                        formik.handleSubmit(event as any);

                        // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                        if (
                          !formik.isValid && // if form invalid
                          !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        ) {
                          const errorTabIndex = getErrorTabIndex(
                            formik.errors,
                            tabToFields
                          );
                          /**
                           * switch to tab containing error
                           *
                           * since form validation is in flight and makes page rendering updates
                           * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                           * putting state changes in a separate tick will prevent coliding page rendering updates
                           *
                           * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                           */
                          setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        }
                      }}
                    >
                      Save
                    </Button>
                  </>
                }
              >
                {/* <ModalWithStyles
            title={queue.queuename || 'Add New Queue'}
            show={show}
            onHide={onHide}
            onSubmit={formik.handleSubmit}
            isLoading={isLoading}
            isSubmitting={formik.isSubmitting}
            disableSubmit={!formik.dirty}
          > */}

                <FormikErrorFocus />
                {/* General Settings */}
                <TabPanel value="0">
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Queue Name</h5>
                      <p className="fieldDescription">
                        Enter a name for your Queue. This will show up on the
                        Kixie Dashboard and in the PowerCall directory. Choose
                        something unique to help agents understand the purpose
                        and members of the queue.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        name="submitQueueEditPayload.name"
                        className={classes.field}
                        formik={formik}
                        // defaultValue={queue.queuename}
                        inputProps={{ maxLength: 45 }}
                        fullWidth
                        label="Queue Name"
                        size="small"
                        required
                        // validate={composeValidators(required())}
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Emails (comma separated)</h5>
                      <p className="fieldDescription">
                        Send Voicemail to (comma separated list)
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        name="submitQueueEditPayload.email"
                        className={classes.field}
                        formik={formik}
                        // defaultValue={queue.email}
                        fullWidth
                        label="Emails (comma separated)"
                        size="small"
                        required
                        type="email"
                        // validate={composeValidators(
                        //   required(),
                        //   commaSeparatedEmails()
                        // )}
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Max Hold Time (min)</h5>
                      <p className="fieldDescription">
                        The maximum time, in minutes, a caller will wait in a
                        queue. If no available agent takes the call before the
                        maximum hold time is reached, the queues No Answer
                        Action will trigger for that caller.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        name="submitQueueEditPayload.maxduration"
                        className={classes.field}
                        formik={formik}
                        // defaultValue={queue.maxduration}
                        fullWidth
                        label="Max Hold Time (min)"
                        size="small"
                        required
                        type="number"
                        // validate={composeValidators(required(), number())}
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Agent Ring Time (sec)</h5>
                      <p className="fieldDescription">
                        The maximum time, in seconds, that an available agent's
                        line will ring before a caller is placed back in the
                        queue. Each individual ring takes around 5 seconds.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        name="submitQueueEditPayload.ringtimeout"
                        className={classes.field}
                        formik={formik}
                        // defaultValue={queue.ringtimeout}
                        fullWidth
                        label="Agent Ring Time (sec)"
                        size="small"
                        required
                        type="number"
                        // validate={composeValidators(required(), number())}
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Play Est. Wait Time</h5>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="updateQueuePayload.playWaitTime"
                            formik={formik}
                            color="primary"
                            // defaultValue={queue.playWaitTime}
                            type="checkbox"
                          />
                        }
                        label="Play Est. Wait Time"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">In Queue Options</h5>
                      <p className="fieldDescription">
                        Present options for callers into your queue to limit or
                        eliminate the amount of time they spend on hold.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="updateQueuePayload.inQueueIVR"
                            formik={formik}
                            color="primary"
                            // defaultValue={queue.inQueueIVR}
                            type="checkbox"
                            onClick={(event: React.ChangeEvent<any>) => {
                              // unchecked dependent options
                              if (!event.target.checked) {
                                formik.setFieldValue(
                                  'updateQueuePayload.inQueueVoicemail',
                                  false
                                );
                                formik.setFieldValue(
                                  'updateQueuePayload.inQueueCallback',
                                  false
                                );
                              }
                            }}
                          />
                        }
                        label="In Queue Options"
                      />
                    </div>
                    <div className="subField">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Leave a Voicemail</h5>
                        <p className="fieldDescription">
                          Allow callers to leave a voicemail message for your
                          queue without needing to wait on hold to speak with
                          someone.
                        </p>
                      </div>
                      <div className="col-lg-4 px-0">
                        <FormControlLabel
                          disabled={
                            !formik.values.updateQueuePayload.inQueueIVR
                          }
                          control={
                            <FormikSwitch
                              name="updateQueuePayload.inQueueVoicemail"
                              formik={formik}
                              color="primary"
                              // defaultValue={queue.inQueueVoicemail}
                              type="checkbox"
                            />
                          }
                          label="Leave a Voicemail"
                        />
                      </div>
                    </div>
                    <div className="subField">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Request Callback</h5>
                        <p className="fieldDescription">
                          Allow callers to request a callback when an agent
                          becomes available. Any agent in the queue who becomes
                          available to handle the call (in the order it was
                          received) will automatically dial out to the caller.
                        </p>
                      </div>
                      <div className="col-lg-4 px-0">
                        <FormControlLabel
                          disabled={
                            !formik.values.updateQueuePayload.inQueueIVR
                          }
                          control={
                            <FormikSwitch
                              name="updateQueuePayload.inQueueCallback"
                              formik={formik}
                              color="primary"
                              // defaultValue={queue.inQueueCallback}
                              type="checkbox"
                            />
                          }
                          label="Request Callback"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">
                        Turn on DND After Missed Call
                      </h5>
                      <p className="fieldDescription">
                        Mitigate missed calls to your queue by automatically
                        switching agents who miss calls to DND, preventing
                        subsequent incoming calls from ringing their PowerCall
                        resulting in additional missed calls. The agent can
                        manually set their status back to available whenever
                        they're ready.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="updateQueuePayload.DndAfterMissedCall"
                            formik={formik}
                            color="primary"
                            // defaultValue={queue.DndAfterMissedCall}
                            type="checkbox"
                          />
                        }
                        label="Turn on DND After Missed Call"
                      />
                    </div>
                  </div>
                  {isSimultaneousRingEnabled && (
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Ring Strategy</h5>
                        <p className="fieldDescription" id="ringStrategyDesc">
                          {formik.values.updateQueuePayload?.ringStrategy ===
                          'simultaneous'
                            ? 'All available agents in the queue will be rung at the same time. The first agent to answer will be connected to the caller.'
                            : 'Agents will be rung one at a time in order. If an agent does not answer, the next available agent will be rung.'}
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormikSelect
                          name="updateQueuePayload.ringStrategy"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Ring Strategy"
                          size="small"
                          variant="outlined"
                          onChange={(event: any) => {
                            if (event.target.value === 'simultaneous') {
                              formik.setFieldValue(
                                'updateQueuePayload.autoAnswerInbound',
                                false
                              );
                              formik.setFieldValue(
                                'updateQueuePayload.autoAnswerReturnedCall',
                                false
                              );
                              const currentNoAnswerType = formik.values.defaultActionPayload?.type;
                              if (currentNoAnswerType && currentNoAnswerType !== 'voicemail' && currentNoAnswerType !== 'hangup') {
                                formik.setFieldValue('defaultActionPayload.type', 'voicemail');
                              }
                            }
                          }}
                        >
                          <MenuItem value="linear">Linear</MenuItem>
                          <MenuItem value="simultaneous">Simultaneous</MenuItem>
                        </FormikSelect>
                      </div>
                    </div>
                  )}

                  {process.env.VITE_APP_FEATURE_AUTO_SUBSCRIBE_QUEUE ===
                    '1' && (
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">
                          Auto-subscribe to Queue on Login
                        </h5>
                        <p className="fieldDescription">
                          Agents are automatically subscribed to the queue as
                          soon as they login to PowerCall, increasing the amount
                          of agents available to handle incoming calls without
                          needing to manually subscribe in PowerCall.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormControlLabel
                          control={
                            <FormikSwitch
                              name="updateQueuePayload.subscribeOnLogin"
                              formik={formik}
                              color="primary"
                              // defaultValue={queue.subscribeOnLogin}
                              type="checkbox"
                            />
                          }
                          label="Auto-subscribe to Queue on Login"
                        />
                      </div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">No Answer Action</h5>
                      <p className="fieldDescription">
                        When a caller in your queue reaches the Max Hold Time,
                        select an option for how to handle that call. Let
                        callers leave a voicemail to provide information for a
                        callback, or route them to a different Kixie agent or
                        entity for a backup team to handle.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <DefaultAction
                        defaultAction={getDefaultActionUrl({
                          type: formik.values.defaultActionPayload
                            ?.type as IActionType,
                          parentType: 'queue',
                          businessid: businessid,
                          user: formik.values.defaultActionPayload?.user,
                          group: formik.values.defaultActionPayload?.group,
                          ivr: formik.values.defaultActionPayload?.ivr,
                          queue: formik.values.defaultActionPayload?.queue,
                          voicemail: '',
                        })}
                        actionType={
                          formik.values.defaultActionPayload
                            ?.type as IActionType
                        }
                        typeName="defaultActionPayload.type"
                        ivrName="defaultActionPayload.ivr"
                        groupName="defaultActionPayload.group"
                        userName="defaultActionPayload.user"
                        queueName="defaultActionPayload.queue"
                        formik={formik}
                        disableFormUpdate={true}
                        id={id}
                        allowedTypes={
                          formik.values.updateQueuePayload?.ringStrategy === 'simultaneous'
                            ? ['voicemail', 'hangup']
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </TabPanel>
                {/* Messages */}
                <TabPanel value="1">
                  <div style={{ marginTop: '16px' }}>
                    <LabelledOutline label="Queue Hours">
                      <div className="row">
                        <div className="col-lg-6">
                          Actions will be routed:
                          {queueHoursString.map((string, index) => (
                            <div key={index}>
                              {string} <br />
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            // paddingRight: '50px',
                            alignItems: 'center',
                          }}
                          className="col-lg-6"
                        >
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="useBusinessHoursEnabled"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Use Business Hours"
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.editButton}
                            onClick={handleShowScheduler}
                            disabled={formik.values.useBusinessHoursEnabled}
                          >
                            Edit Queue Hours
                          </Button>
                        </div>
                      </div>
                    </LabelledOutline>
                    <DefaultAction
                      defaultAction={getDefaultActionUrl({
                        type: formik.values.closedActionPayload
                          ?.type as IActionType,
                        parentType: 'queue',
                        businessid: businessid,
                        user: formik.values.closedActionPayload?.user,
                        group: formik.values.closedActionPayload?.group,
                        ivr: formik.values.closedActionPayload?.ivr,
                        queue: formik.values.closedActionPayload?.queue,
                        voicemail: '',
                      })}
                      label="Outside Queue Hours Action"
                      labelForBusiness="Outside Business Hours Action"
                      actionType={
                        formik.values.closedActionPayload?.type as IActionType
                      }
                      typeName="closedActionPayload.type"
                      ivrName="closedActionPayload.ivr"
                      groupName="closedActionPayload.group"
                      userName="closedActionPayload.user"
                      queueName="closedActionPayload.queue"
                      formik={formik}
                      disableFormUpdate={true}
                      id={id}
                    />
                  </div>
                  <ModalWithStyles
                    onSubmit={handleSubmit}
                    title={'Business Hours'}
                    show={showScheduler}
                    onHide={onHideHours}
                    isSubmitting={formik.isSubmitting}
                    size="lg"
                  >
                    <Scheduler
                      schedulableHours={formik.values.queueHours}
                      setSchedulableHours={handleSetSchedulableHours}
                    />
                  </ModalWithStyles>
                </TabPanel>
                <TabPanel value="2">
                  <article>
                    <RecordNow
                      dropdownLabel="Queue Greeting Type"
                      dropdownName="submitQueueEditPayload.greetingType"
                      dropdownType={
                        formik.values.submitQueueEditPayload?.greetingType
                      }
                      outlineLabel="Queue Greeting"
                      ttsLabel="Queue Greeting"
                      ttsName="submitQueueEditPayload.greeting"
                      filenameName="submitQueueGreetingMp3File.files"
                      filesizeName="submitQueueGreetingMp3File.name"
                      formik={formik}
                      filename={filenames.greeting}
                      filepath={filepaths.greeting}
                      onChangeFile={onChangeFile('greeting')}
                      onRecording={handleRecording('greeting')}
                    >
                      <MenuItem value="none">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="tts">Text-to-Speech</MenuItem>
                      <MenuItem value="mp3">MP3</MenuItem>
                      <MenuItem value="record">Record Now</MenuItem>
                    </RecordNow>

                    <RecordNow
                      dropdownLabel="Queue Voicemail Type"
                      dropdownName="submitQueueEditPayload.voicemailType"
                      dropdownType={
                        formik.values.submitQueueEditPayload?.voicemailType
                      }
                      outlineLabel="Queue Voicemail"
                      ttsLabel="Queue Voicemail"
                      ttsName="submitQueueEditPayload.voicemail"
                      filenameName="submitQueueVoicemailMp3File.files"
                      filesizeName="submitQueueVoicemailMp3File.name"
                      formik={formik}
                      filename={filenames.voicemail}
                      filepath={filepaths.voicemail}
                      onChangeFile={onChangeFile('voicemail')}
                      onRecording={handleRecording('voicemail')}
                    >
                      <MenuItem value="tts">Text-to-Speech</MenuItem>
                      <MenuItem value="mp3">MP3</MenuItem>
                      <MenuItem value="record">Record Now</MenuItem>
                    </RecordNow>
                    {/* <FormikSelect
                    name="submitQueueEditPayload.musicType"
                    className={classes.field}
                    formik={formik}
                    // defaultValue={queue.musicType}
                    fullWidth
                    label="Queue Hold Music Type"
                    size="small"
                    variant="outlined"
                  >
                    <MenuItem value="mp3">MP3</MenuItem>
                  </FormikSelect> */}
                    <RecordNow
                      dropdownLabel="Queue Hold Music"
                      dropdownName="submitQueueEditPayload.musicType"
                      dropdownType={
                        formik.values.submitQueueEditPayload?.musicType
                      }
                      outlineLabel="Hold Music"
                      ttsLabel="Queue Music"
                      ttsName="submitQueueEditPayload.holdMusic"
                      filenameName="submitQueueHoldMusicMp3File.files"
                      filesizeName="submitQueueHoldMusicMp3File.name"
                      formik={formik}
                      filename={filenames.holdMusic}
                      filepath={filepaths.holdMusic}
                      onChangeFile={onChangeFile('holdMusic')}
                      onRecording={handleRecording('holdMusic')}
                    >
                      <MenuItem value="none">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="mp3">MP3</MenuItem>
                    </RecordNow>
                  </article>
                </TabPanel>

                {/* Queue Members */}
                <TabPanel value="3">
                  <article>
                    <QueueTeams
                      id={id}
                      formik={formik}
                      onTeamChange={onTeamChange}
                    />
                  </article>
                </TabPanel>
                <TabPanel value="4">
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">AutoAnswer Calls</h5>
                      <p className="fieldDescription">
                        Instead of receiving a prompt to accept/reject incoming
                        calls, available agents will immediately be connected to
                        the next caller in the queue
                        {formik.values.updateQueuePayload.ringStrategy ===
                          'simultaneous' && (
                          <strong>
                            {' '}
                            (Disabled when Ring Strategy is Simultaneous)
                          </strong>
                        )}
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <Tooltip
                        title={
                          formik.values.updateQueuePayload.ringStrategy ===
                          'simultaneous'
                            ? 'Auto-answer is not available when ringing all agents simultaneously'
                            : ''
                        }
                        placement="top"
                      >
                        <span>
                          <FormControlLabel
                            disabled={
                              formik.values.updateQueuePayload.ringStrategy ===
                              'simultaneous'
                            }
                            control={
                              <FormikSwitch
                                name="updateQueuePayload.autoAnswerInbound"
                                formik={formik}
                                color="primary"
                                // defaultValue={queue.autoAnswerInbound}
                                type="checkbox"
                              />
                            }
                            label="AutoAnswer Calls"
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Auto-answer Callbacks</h5>
                      <p className="fieldDescription">
                        Instead of receiving a prompt to initiate a callback,
                        the dialer of the next available agent will immediately
                        start calling
                        {formik.values.updateQueuePayload.ringStrategy ===
                          'simultaneous' && (
                          <strong>
                            {' '}
                            (Disabled when Ring Strategy is Simultaneous)
                          </strong>
                        )}
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <Tooltip
                        title={
                          formik.values.updateQueuePayload.ringStrategy ===
                          'simultaneous'
                            ? 'Auto-answer is not available when ringing all agents simultaneously'
                            : ''
                        }
                        placement="top"
                      >
                        <span>
                          <FormControlLabel
                            disabled={
                              formik.values.updateQueuePayload.ringStrategy ===
                              'simultaneous'
                            }
                            control={
                              <FormikSwitch
                                name="updateQueuePayload.autoAnswerReturnedCall"
                                formik={formik}
                                color="primary"
                                // defaultValue={queue.autoAnswerReturnedCall}
                                type="checkbox"
                              />
                            }
                            label="Auto-answer Callbacks"
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Assign Ownership on Answer</h5>
                      <p className="fieldDescription">
                        If a contact is not in a connected CRM, or if the
                        contact has no owner in the CRM, the contact will be
                        assigned to whoever answers the call
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="updateQueuePayload.assignOwnerOnAnswer"
                            formik={formik}
                            color="primary"
                            // defaultValue={queue.assignOwnerOnAnswer}
                            type="checkbox"
                          />
                        }
                        label="Assign Ownership on Answer"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Override Owner on Answer</h5>
                      <p className="fieldDescription">
                        If a contact is not in a connected CRM, or if the
                        contact has no owner in the CRM, the contact will be
                        assigned to whoever answers the call
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="updateQueuePayload.overrideOwnerOnAnswer"
                            formik={formik}
                            color="primary"
                            // defaultValue={queue.overrideOwnerOnAnswer}
                            type="checkbox"
                          />
                        }
                        label="Override Owner on Answer"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Initial Attempt Behavior</h5>
                      <p className="fieldDescription" id="initAttemptDesc">
                        Choose an option to determine how calls to the queue
                        respect contact ownership in your connected CRM
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikSelect
                        name="updateQueuePayload.initialAttempt"
                        className={classes.field}
                        formik={formik}
                        fullWidth
                        label="Initial Attempt Behavior"
                        size="small"
                        variant="outlined"
                        onChange={(event: any) => {
                          const desc = document.getElementById(
                            'initAttemptDesc'
                          );
                          if (event.target.value === 'ignoreOwnership') {
                            desc!.innerText =
                              'Calls will route to the next available agent, regardless of contact ownership in your connected CRM';
                          } else if (
                            event.target.value === 'routeToOwnerOnly'
                          ) {
                            desc!.innerText =
                              "If the CRM contact owner is in the queue, the call will only route to them. If they don't answer, it won't route to other agents";
                          } else if (
                            event.target.value === 'routeToOwnerThenProceed'
                          ) {
                            desc!.innerText =
                              "If the CRM contact owner is in the queue and doesn't answer, the call will route to other available agents";
                          }
                        }}
                      >
                        <MenuItem value="ignoreOwnership">
                          Ignore Ownership
                        </MenuItem>
                        <MenuItem value="routeToOwnerOnly">
                          Route To Owner Only
                        </MenuItem>
                        <MenuItem value="routeToOwnerThenProceed">
                          Route To Owner Then Proceed To Add to List
                        </MenuItem>
                      </FormikSelect>
                    </div>
                  </div>
                </TabPanel>

                {/* add hidden button to allow enter page */}
                <button hidden={true}>submit</button>
                {/* </ModalWithStyles> */}
              </WizardModal>
            </TabContext>
          </form>
        );
      }}
    </Formik>
  );
}
